import { addDays, differenceInCalendarDays, parseISO } from 'date-fns'

export interface PricingGrid {
  headers: string[]
  content: string[][]
}

export interface PriceListModel {
  id?: string
  name: string
  pricing: PricingGrid
}

export type PriceListGridKey = 'pricing'

export type PriceListFormModel = PriceListModel & {
  loading: boolean
}

export const emptyGrid = (): PricingGrid => ({ headers: [], content: [] })

export const START_DATE_COLUMN = 'start_date'
export const END_DATE_COLUMN = 'end_date'

export const DATE_COLUMNS = [START_DATE_COLUMN, END_DATE_COLUMN]

export const isDateColumn = (header: string): boolean =>
  DATE_COLUMNS.includes((header ?? '').toLowerCase())

export type PriceListColumnType = 'impressions' | 'audience'

export const PRICE_LIST_COLUMN_TYPES: PriceListColumnType[] = ['impressions', 'audience']

// The media_format / offer combinations supported by the backend pricing plans.
// A new price list is seeded with every combination so the user can fill in
// values for all of them.
export const MEDIA_FORMATS = ['DG_CL', 'DG_IN', 'DG_CS'] as const
export const OFFERS = [
  'run_on_network',
  'agglomerations',
  'buildings',
  'media_selection',
  'network_g14_plus',
  'network_a14_plus',
] as const

// Separator used to encode the type into a value column header, e.g.
// `impressions::DG_CL||run_on_network`. The double colon can't appear in the
// value column keys.
export const TYPE_SEPARATOR = '::'

// Separator packing a media_format and an offer into a single value column key,
// e.g. `DG_CL||run_on_network`. Neither media formats nor offers contain it.
export const MEDIA_FORMAT_OFFER_SEPARATOR = '||'

// Builds a value column key out of a media format and an offer.
export const valueColumnKey = (mediaFormat: string, offer: string): string =>
  `${mediaFormat}${MEDIA_FORMAT_OFFER_SEPARATOR}${offer}`

// Extracts the media format from a value column key.
export const mediaFormatOfColumn = (valueKey: string): string =>
  (valueKey ?? '').split(MEDIA_FORMAT_OFFER_SEPARATOR)[0] ?? ''

// Extracts the offer from a value column key.
export const offerOfColumn = (valueKey: string): string =>
  (valueKey ?? '').split(MEDIA_FORMAT_OFFER_SEPARATOR)[1] ?? ''

// Human readable label for a value column key shown in the group header.
export const formatValueColumn = (valueKey: string): string => {
  const mediaFormat = mediaFormatOfColumn(valueKey)
  const offer = offerOfColumn(valueKey)
  return offer ? `${mediaFormat} · ${offer}` : mediaFormat
}

// The full ordered set of value columns for a fresh price list.
export const defaultValueColumns = (): string[] =>
  MEDIA_FORMATS.flatMap(mediaFormat => OFFERS.map(offer => valueColumnKey(mediaFormat, offer)))

// Builds a typed value column header, e.g. typedColumn('audience', 'DG_CL||buildings')
// => 'audience::DG_CL||buildings'.
export const typedColumn = (type: PriceListColumnType, column: string): string =>
  `${type}${TYPE_SEPARATOR}${column}`

// Returns the type encoded in a value column header, or null for date columns.
export const columnType = (header: string): PriceListColumnType | null => {
  const [prefix] = (header ?? '').split(TYPE_SEPARATOR)
  return PRICE_LIST_COLUMN_TYPES.includes(prefix as PriceListColumnType)
    ? (prefix as PriceListColumnType)
    : null
}

// Strips the type prefix from a value column header, e.g.
// 'audience::DG_CL||buildings' => 'DG_CL||buildings'.
export const columnLabel = (header: string): string => {
  const separatorIndex = (header ?? '').indexOf(TYPE_SEPARATOR)
  return separatorIndex === -1 ? header : header.slice(separatorIndex + TYPE_SEPARATOR.length)
}

// Builds the doubled header set: shared date columns followed by each value
// column grouped together, with its impressions and audience cells adjacent
// (e.g. DG_CL||buildings -> impressions::DG_CL||buildings, audience::DG_CL||buildings).
// This is where the backend's separate impressions/audience pricings get
// consolidated into a single grid.
export const buildDoubledHeaders = (valueColumns: string[]): string[] => [
  ...DATE_COLUMNS,
  ...valueColumns.flatMap(column => PRICE_LIST_COLUMN_TYPES.map(type => typedColumn(type, column))),
]

export const isValidFloatCell = (value: string): boolean =>
  /^-?\d+(\.\d+)?$/.test((value ?? '').trim())

// Value cells must be numbers strictly greater than 0.
export const isPositiveFloatCell = (value: string): boolean =>
  isValidFloatCell(value) && parseFloat(value.trim()) > 0

// A value cell is valid when it's left empty (no price set) or holds a
// strictly positive number.
export const isValidValueCell = (value: string): boolean =>
  (value ?? '').trim() === '' || isPositiveFloatCell(value)

export const isValidDateCell = (value: string): boolean => (value ?? '').trim() !== ''

// Allows intermediate states while typing (e.g. '', '-', '1.', '.5')
// but blocks letters and other disallowed characters.
export const isFloatInput = (value: string): boolean => /^-?\d*\.?\d*$/.test(value)

// A fresh period: empty dates, zeroed prices.
export const makeBlankRow = (headers: string[]): string[] =>
  headers.map(header => (isDateColumn(header) ? '' : '0'))

// The first row is the default period. It has no dates and is used
// as the fallback price for any period not covered by a configured row.
export const DEFAULT_PERIOD_ROW_INDEX = 0

export const isDefaultPeriodRow = (rowIndex: number): boolean =>
  rowIndex === DEFAULT_PERIOD_ROW_INDEX

interface Period {
  start: number
  end: number
}

const getDateColumnIndexes = (headers: string[]): { startIdx: number; endIdx: number } => ({
  startIdx: headers.findIndex(header => (header ?? '').toLowerCase() === START_DATE_COLUMN),
  endIdx: headers.findIndex(header => (header ?? '').toLowerCase() === END_DATE_COLUMN),
})

const toTime = (value: string): number | null => {
  const time = new Date((value ?? '').trim()).getTime()
  return Number.isNaN(time) ? null : time
}

const getRowPeriod = (row: string[], startIdx: number, endIdx: number): Period | null => {
  const start = toTime(row[startIdx])
  const end = toTime(row[endIdx])
  return start === null || end === null ? null : { start, end }
}

const isPeriodOrderValid = (period: Period): boolean => period.start <= period.end

const periodsOverlap = (a: Period, b: Period): boolean => a.start <= b.end && b.start <= a.end

// Returns the indexes of rows whose period is reversed (start after end)
// or overlaps another row's period.
export const getInvalidPeriodRows = (grid: PricingGrid): Set<number> => {
  const invalid = new Set<number>()
  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  if (startIdx < 0 || endIdx < 0) {
    return invalid
  }

  const periods = grid.content.map(row => getRowPeriod(row, startIdx, endIdx))

  periods.forEach((period, i) => {
    if (!period || isDefaultPeriodRow(i)) {
      return
    }

    if (!isPeriodOrderValid(period)) {
      invalid.add(i)
      return
    }

    periods.forEach((other, j) => {
      if (i === j || !other || !isPeriodOrderValid(other)) {
        return
      }
      if (periodsOverlap(period, other)) {
        invalid.add(i)
        invalid.add(j)
      }
    })
  })

  return invalid
}

interface DatePeriod {
  start: Date
  end: Date
}

const parseCellDate = (value: string): Date | null => {
  if (!isValidDateCell(value)) {
    return null
  }
  const date = parseISO(value.trim())
  return Number.isNaN(date.getTime()) ? null : date
}

// Configured periods (both dates set) other than the given row.
const getOtherDatePeriods = (grid: PricingGrid, excludeRow: number): DatePeriod[] => {
  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  if (startIdx < 0 || endIdx < 0) {
    return []
  }

  return grid.content.reduce<DatePeriod[]>((periods, row, rowIndex) => {
    if (rowIndex === excludeRow || isDefaultPeriodRow(rowIndex)) {
      return periods
    }
    const start = parseCellDate(row[startIdx])
    const end = parseCellDate(row[endIdx])
    if (start && end) {
      periods.push({ start, end })
    }
    return periods
  }, [])
}

const datePeriodsOverlap = (a: DatePeriod, b: DatePeriod): boolean =>
  differenceInCalendarDays(a.start, b.end) <= 0 && differenceInCalendarDays(b.start, a.end) <= 0

// Decides whether `day` should be disabled in the date picker of a given
// row/column. It blocks days that would overlap another period and keeps
// the period proper (end not before start, start not after end).
export const isPeriodDayDisabled = (
  grid: PricingGrid,
  rowIndex: number,
  col: number,
  day: Date
): boolean => {
  if (isDefaultPeriodRow(rowIndex)) {
    return false
  }

  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  if (startIdx < 0 || endIdx < 0) {
    return false
  }

  const row = grid.content[rowIndex] ?? []

  // Always keep the day already set in this cell selectable, so existing
  // values (even past ones) stay visible and removable.
  const currentValue = parseCellDate(row[col])
  if (currentValue && differenceInCalendarDays(day, currentValue) === 0) {
    return false
  }

  // Past dates can't be selected.
  if (differenceInCalendarDays(day, new Date()) < 0) {
    return true
  }

  let candidate: DatePeriod

  if (col === startIdx) {
    const siblingEnd = parseCellDate(row[endIdx])
    if (siblingEnd && differenceInCalendarDays(day, siblingEnd) > 0) {
      return true
    }
    candidate = { start: day, end: siblingEnd ?? day }
  } else {
    const siblingStart = parseCellDate(row[startIdx])
    if (siblingStart && differenceInCalendarDays(day, siblingStart) < 0) {
      return true
    }
    candidate = { start: siblingStart ?? day, end: day }
  }

  return getOtherDatePeriods(grid, rowIndex).some(period => datePeriodsOverlap(candidate, period))
}

// A new period row whose start date defaults to the day after the latest
// configured period's end date (end left empty for the user to fill).
export const makeNextPeriodRow = (grid: PricingGrid): string[] => {
  const row = makeBlankRow(grid.headers)
  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  if (startIdx < 0 || endIdx < 0) {
    return row
  }

  const latestEnd = grid.content.reduce<Date | null>((latest, currentRow, rowIndex) => {
    if (isDefaultPeriodRow(rowIndex)) {
      return latest
    }
    const end = parseCellDate(currentRow[endIdx])
    if (!end) {
      return latest
    }
    return latest === null || end > latest ? end : latest
  }, null)

  if (latestEnd) {
    row[startIdx] = addDays(latestEnd, 1).toISOString()
  }

  return row
}

// A configured period whose end date is already in the past.
export const isPastPeriodRow = (grid: PricingGrid, rowIndex: number): boolean => {
  if (isDefaultPeriodRow(rowIndex)) {
    return false
  }
  const { endIdx } = getDateColumnIndexes(grid.headers)
  if (endIdx < 0) {
    return false
  }
  const end = parseCellDate(grid.content[rowIndex]?.[endIdx])
  return end !== null && differenceInCalendarDays(end, new Date()) < 0
}

// Earliest start date and latest end date across configured periods.
// Both are null when only the default period is present.
export const getPeriodDateRange = (
  grid: PricingGrid
): { startDate: Date | null; endDate: Date | null } => {
  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  if (startIdx < 0 || endIdx < 0) {
    return { startDate: null, endDate: null }
  }

  let startDate: Date | null = null
  let endDate: Date | null = null

  grid.content.forEach((row, rowIndex) => {
    if (isDefaultPeriodRow(rowIndex)) {
      return
    }
    const start = parseCellDate(row[startIdx])
    const end = parseCellDate(row[endIdx])
    if (start && (startDate === null || start < startDate)) {
      startDate = start
    }
    if (end && (endDate === null || end > endDate)) {
      endDate = end
    }
  })

  return { startDate, endDate }
}

// -- Backend <-> grid conversion --------------------------------------------
// The backend keeps a flat list of `pricings`, each scoped to a single target
// (`impressions` or `audience`) and an optional date range. The grid, on the
// other hand, consolidates both targets of a period into a single row. These
// helpers translate between the two representations.

export interface MediaFormatOfferPricing {
  mediaFormat: string
  offer: string
  valueCents: number
}

export interface Pricing {
  id?: string
  target: string
  name?: string | null
  startDate?: string | null
  endDate?: string | null
  mediaFormatOfferPricings: MediaFormatOfferPricing[]
}

export interface PricingInput {
  target: string
  name?: string | null
  startDate?: string | null
  endDate?: string | null
  mediaFormatOfferPricings: MediaFormatOfferPricing[]
}

export interface PricingPlanNode {
  id: string
  name: string
  default?: boolean
  pricings: Pricing[]
}

const centsToCell = (valueCents: number): string => (valueCents / 100).toString()

const cellToCents = (cell: string): number => {
  const trimmed = (cell ?? '').trim()
  return trimmed === '' ? 0 : Math.round(parseFloat(trimmed) * 100)
}

// A period groups the (at most two) pricings sharing the same date range.
const periodKey = (startDate?: string | null, endDate?: string | null): string =>
  `${startDate ?? ''}__${endDate ?? ''}`

// Value columns present in the backend payload, ordered by the canonical
// media format / offer order and keeping any unexpected extras at the end.
const deriveValueColumns = (pricings: Pricing[]): string[] => {
  const found = new Set<string>()
  pricings.forEach(pricing =>
    (pricing.mediaFormatOfferPricings ?? []).forEach(entry =>
      found.add(valueColumnKey(entry.mediaFormat, entry.offer))
    )
  )

  if (found.size === 0) {
    return defaultValueColumns()
  }

  const canonical = defaultValueColumns().filter(column => found.has(column))
  const extras = [...found].filter(column => !canonical.includes(column))
  return [...canonical, ...extras]
}

// Builds a media_format||offer -> valueCents lookup for a single pricing.
const pricingValueMap = (pricing?: Pricing): Map<string, number> => {
  const map = new Map<string, number>()
  ;(pricing?.mediaFormatOfferPricings ?? []).forEach(entry =>
    map.set(valueColumnKey(entry.mediaFormat, entry.offer), entry.valueCents)
  )
  return map
}

// Turns the backend flat pricings list into the consolidated editor grid.
export const pricingsToGrid = (pricings: Pricing[] = []): PricingGrid => {
  const valueColumns = deriveValueColumns(pricings)
  const headers = buildDoubledHeaders(valueColumns)

  const periods = new Map<
    string,
    { startDate: string; endDate: string; byTarget: Partial<Record<PriceListColumnType, Pricing>> }
  >()

  pricings.forEach(pricing => {
    const key = periodKey(pricing.startDate, pricing.endDate)
    const period = periods.get(key) ?? {
      startDate: pricing.startDate ?? '',
      endDate: pricing.endDate ?? '',
      byTarget: {},
    }
    const target = columnType(pricing.target) ?? (pricing.target as PriceListColumnType)
    period.byTarget[target] = pricing
    periods.set(key, period)
  })

  const orderedPeriods = [...periods.values()].sort((a, b) => {
    const aDefault = !a.startDate && !a.endDate
    const bDefault = !b.startDate && !b.endDate
    if (aDefault !== bDefault) {
      return aDefault ? -1 : 1
    }
    return (a.startDate || '').localeCompare(b.startDate || '')
  })

  // Guarantee a default (fallback) period row even if the backend omitted it.
  if (!orderedPeriods.some(period => !period.startDate && !period.endDate)) {
    orderedPeriods.unshift({ startDate: '', endDate: '', byTarget: {} })
  }

  const content = orderedPeriods.map(period => {
    const valueMaps: Record<PriceListColumnType, Map<string, number>> = {
      impressions: pricingValueMap(period.byTarget.impressions),
      audience: pricingValueMap(period.byTarget.audience),
    }

    const row = [period.startDate, period.endDate]
    valueColumns.forEach(valueKey => {
      PRICE_LIST_COLUMN_TYPES.forEach(type => {
        const cents = valueMaps[type].get(valueKey)
        // No entry (or an explicit 0, never a valid price) is shown as an
        // empty cell rather than '0' so it round-trips through edits as empty.
        row.push(!cents ? '' : centsToCell(cents))
      })
    })
    return row
  })

  return { headers, content }
}

// Turns the consolidated editor grid back into the backend flat pricings list,
// emitting one pricing per target for every period row.
export const gridToPricings = (grid: PricingGrid): PricingInput[] => {
  const { startIdx, endIdx } = getDateColumnIndexes(grid.headers)
  const valueColumns = [
    ...new Set(
      grid.headers
        .filter(header => !isDateColumn(header) && columnType(header) !== null)
        .map(header => columnLabel(header))
    ),
  ]

  const pricings: PricingInput[] = []

  grid.content.forEach((row, rowIndex) => {
    const isDefaultRow = isDefaultPeriodRow(rowIndex)
    const startDate = isDefaultRow ? null : row[startIdx] || null
    const endDate = isDefaultRow ? null : row[endIdx] || null

    PRICE_LIST_COLUMN_TYPES.forEach(target => {
      const mediaFormatOfferPricings: MediaFormatOfferPricing[] = valueColumns.map(valueKey => {
        const col = grid.headers.indexOf(typedColumn(target, valueKey))
        return {
          mediaFormat: mediaFormatOfColumn(valueKey),
          offer: offerOfColumn(valueKey),
          valueCents: col >= 0 ? cellToCents(row[col]) : 0,
        }
      })

      pricings.push({ target, startDate, endDate, mediaFormatOfferPricings })
    })
  })

  return pricings
}
